import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Banknote, Loader2, FileText, CheckCircle2, AlertCircle, Plus, Upload } from "lucide-react";
import { fmtUSDate } from "../lib/date";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending:  { label: "Pending review", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
  approved: { label: "Approved",       cls: "bg-blue-100 text-blue-800 border border-blue-300" },
  paid:     { label: "Paid",           cls: "bg-emerald-100 text-emerald-800 border border-emerald-300" },
  rejected: { label: "Rejected",       cls: "bg-red-100 text-red-800 border border-red-300" },
};

function formatUSD(n) {
  return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function VendorPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    invoice_number: "",
    amount: "",
    description: "",
    due_date: "",
    file: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [iRes, lRes] = await Promise.all([
        axios.get(`${API_BASE}/vendor-portal/${token}`),
        axios.get(`${API_BASE}/vendor-portal/${token}/invoices`),
      ]);
      setInfo(iRes.data);
      setInvoices(lRes.data || []);
    } catch (e) {
      const detail = e.response?.data?.detail || "Invalid or revoked link.";
      setError(typeof detail === "string" ? detail : "Invalid link");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("invoice_number", form.invoice_number);
      fd.append("amount", amt);
      fd.append("description", form.description);
      fd.append("due_date", form.due_date);
      if (form.file) fd.append("file", form.file);
      await axios.post(`${API_BASE}/vendor-portal/${token}/invoices`, fd);
      toast.success("Invoice submitted");
      setForm({ invoice_number: "", amount: "", description: "", due_date: "", file: null });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="bg-white border border-red-200 rounded-sm p-8 text-center max-w-md" data-testid="vendor-portal-error">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Link unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-3">Contact the property manager who shared this link.</p>
        </div>
      </div>
    );
  }

  const total = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const pending = invoices.filter((i) => i.status === "pending").length;
  const paid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="vendor-portal">
        <div className="mb-6 sm:mb-8">
          <div className="overline text-slate-500 mb-1">Vendor portal</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            {info?.vendor?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Submit invoices to <span className="font-semibold">{info?.landlord?.name}</span> and track their status.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Stat label="Total submitted" value={formatUSD(total)} testid="portal-stat-total" />
          <Stat label="Pending review" value={pending.toString()} testid="portal-stat-pending" />
          <Stat label="Paid YTD" value={formatUSD(paid)} testid="portal-stat-paid" />
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="overline text-slate-500">Your invoices</div>
          <button
            onClick={() => setShowForm((s) => !s)}
            data-testid="portal-toggle-form"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-sm"
          >
            <Plus className="w-4 h-4" /> {showForm ? "Cancel" : "Submit invoice"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-5 mb-6 space-y-3" data-testid="portal-invoice-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Invoice number">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="portal-invoice-number"
                  maxLength={64}
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  placeholder="INV-1042"
                />
              </Field>
              <Field label="Amount (USD)">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="input-clean w-full pl-7"
                    data-testid="portal-invoice-amount"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </Field>
            </div>
            <Field label="Description">
              <textarea
                rows={2}
                maxLength={1000}
                className="input-clean w-full text-sm"
                data-testid="portal-invoice-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What was this work?"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Due date (optional)">
                <input
                  type="date"
                  className="input-clean w-full"
                  data-testid="portal-invoice-due"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                />
              </Field>
              <Field label="Attach PDF / image (optional)">
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  className="block w-full text-sm text-slate-600"
                  data-testid="portal-invoice-file"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                />
              </Field>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="portal-invoice-submit"
                className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Submit invoice
              </button>
            </div>
          </form>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {invoices.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="portal-empty">
              <FileText className="w-8 h-8 mx-auto text-slate-300 mb-3" />
              <div className="font-semibold text-slate-700">No invoices submitted yet.</div>
              <div className="text-sm mt-1">Click "Submit invoice" to send your first one.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-left px-4 py-3">Invoice #</th>
                  <th className="text-left px-4 py-3">Description</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3 pl-6">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((i) => {
                  const badge = STATUS_BADGE[i.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`portal-invoice-row-${i.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{fmtUSDate(i.submitted_at)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{i.invoice_number || `${i.id.slice(0, 8)}…`}</td>
                      <td className="px-4 py-3 text-slate-700 truncate max-w-[260px]">{i.description || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{formatUSD(i.amount)}</td>
                      <td className="px-4 py-3 pl-6">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${badge.cls}`}>
                          {badge.label}
                        </span>
                        {i.status === "rejected" && i.rejection_reason && (
                          <div className="text-xs text-red-700 mt-1 italic">{i.rejection_reason}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-slate-400 mt-8 flex items-center justify-center gap-1">
          <Banknote className="w-3 h-3" /> Powered by RealtyVault Manager
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="overline text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold font-mono text-slate-900">{value}</div>
    </div>
  );
}
